/* Margin Finder · o que as quatro sessões compartilham.
 *
 * Com duas sessões a duplicação era tolerável; com quatro (telecom, seguros, saúde,
 * assinaturas) deixou de ser. O que mora aqui é o esqueleto — a query, o <select>, o
 * cartão de plano, o rodapé de procedência — e NÃO a prosa: cada agente tem o seu
 * número-título e os seus rótulos, e achatar isso num componente só faria a tela
 * mentir sobre qual é a pergunta daquela aba.
 *
 * Todo dinheiro destas telas passa por `brl2`, nunca por `compact()`: R$ 902,50
 * viraria "903" e R$ 10.830,00 viraria "10,8 mil". Para o número que justifica a aba,
 * perder os centavos é regressão — é por isso que o herói não usa <Tiles>.
 */

import { useEffect } from "react";
import type { ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import type { MargemBase, MargemPayload } from "../../lib/api";
import { brl2, fmtDateTime, shortId } from "../../lib/format";
import { Card, Empty, ErrorState, Skeleton } from "../../components/ui";
import { AgentsRefresh } from "../../components/AgentsRefresh";
import { useUsers } from "../../components/Shell";

/* ── rótulos e formato ─────────────────────────────────────────────────────
 * Nenhum snake_case chega à tela, e toda leitura é `MAPA[k] ?? k`: um schema com um
 * valor novo mostra o valor cru, nunca um espaço em branco. */

export const rotulo = (mapa: Record<string, string>, v: string | null | undefined) =>
  v == null ? "—" : mapa[v] ?? v;

export const CONFIANCA: Record<string, string> = {
  alta: "confiança alta",
  media: "confiança média",
  baixa: "confiança baixa",
  sem_evidencia: "sem evidência",
  NENHUMA: "não identificada",
};

/** Dinheiro com centavos, ou travessão. Nunca "R$ 0,00" para ausência de dado. */
export const money = (v: number | null | undefined) =>
  v == null ? "—" : `R$ ${brl2(v)}`;

/** Texto do agente, ou travessão — nunca string vazia nem "null" na tela. */
export const ou = (v: string | null | undefined, fallback = "—") =>
  v == null || v === "" ? fallback : v;

/** Toda lista do agente passa por aqui: `null` e objeto viram `[]`. */
export const lista = <T,>(v: T[] | null | undefined): T[] => (Array.isArray(v) ? v : []);

/** O domínio de uma fonte, para o link não ocupar a largura da tela. */
export const host = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

/* ── blocos de apresentação ──────────────────────────────────────────────── */

export function Facts({ items }: { items: { k: string; v: string; tag?: string }[] }) {
  return (
    <div className="mf-facts">
      {items.map((f) => (
        <div className="mf-fact" key={f.k}>
          <div className="k">{f.k}</div>
          <div className="v">{f.tag ? <span className={f.tag}>{f.v}</span> : f.v}</div>
        </div>
      ))}
    </div>
  );
}

/** O herói: a moldura e o `data-estado`, nada mais. Os quatro agentes discordam em
 *  qual número manda, então título, valor e coluna lateral vêm de fora. */
export function Hero({ estado, titulo, valor, detalhe, lado, faixa }: {
  estado: string;
  titulo: string;
  valor: ReactNode;
  detalhe?: ReactNode;
  lado?: { k: string; v: ReactNode; d?: ReactNode };
  faixa?: boolean;
}) {
  return (
    <div className="mf-hero" data-estado={estado}>
      <div className="mf-hero-main">
        <div className="k">{titulo}</div>
        <div className={faixa ? "v mf-hero-faixa" : "v"}>{valor}</div>
        {detalhe && <div className="d">{detalhe}</div>}
      </div>
      {lado && (
        <div className="mf-hero-side">
          <div className="k">{lado.k}</div>
          <div className="v2">{lado.v}</div>
          {lado.d && <div className="d">{lado.d}</div>}
        </div>
      )}
    </div>
  );
}

/** A moldura do cartão de opção. Telecom, saúde e a carteira de seguros são todos
 *  "uma opção com preço, motivo e ressalva" — mas o miolo numérico não tem interseção
 *  nenhuma (GB e promoção vs acomodação e IDSS vs capital segurado), então ele entra
 *  como `children` em vez de virar um union de props. */
export function PlanoShell({
  papel, escolhido, titulo, sub, preco, precoNota, chips, children, porQue, atencao, fonteUrl,
}: {
  papel: string;
  escolhido?: boolean;
  titulo: string;
  sub?: ReactNode;
  preco: string;
  precoNota?: string;
  chips?: ReactNode;
  children?: ReactNode;
  porQue?: string | null;
  atencao?: string | null;
  fonteUrl?: string | null;
}) {
  return (
    <article className="mf-plan" data-escolhido={escolhido ? "true" : undefined}>
      <div className="mf-plan-papel">{papel}</div>
      <h3>{titulo}</h3>
      {sub && <div className="mf-plan-linha">{sub}</div>}
      <div className="preco">
        {preco}
        {precoNota && <small> {precoNota}</small>}
      </div>
      {children}
      {chips && <div className="chips">{chips}</div>}
      {porQue && <p className="mf-nota">{porQue}</p>}
      {atencao && <p className="mf-atencao">{atencao}</p>}
      {fonteUrl && (
        <a className="mf-source" href={fonteUrl} target="_blank" rel="noreferrer">
          fonte: {host(fonteUrl)}
        </a>
      )}
    </article>
  );
}

/** Barras horizontais comparando valores. O gráfico de método das quatro telas: em
 *  todas a pergunta é "quanto cada opção custa contra o que sai hoje". */
export function Barras({ rows }: {
  rows: { key: string; rotulo: string; valor: number; qtd?: string; destaque?: boolean }[];
}) {
  const teto = Math.max(...rows.map((r) => r.valor), 0) || 1;
  return (
    <div className="mf-cats">
      {rows.map((r) => (
        <div className="mf-cat" key={r.key}>
          <span className="rot">{r.rotulo}</span>
          <span className="barra">
            <span style={{
              width: `${(r.valor / teto) * 100}%`,
              background: r.destaque ? "var(--despesa)" : "var(--accent)",
            }} />
          </span>
          <span className="val">{money(r.valor)}</span>
          <span className="qtd">{r.qtd ?? ""}</span>
        </div>
      ))}
    </div>
  );
}

/** Os cinco campos de auditoria que os quatro agentes têm em comum. O que é próprio
 *  de cada um entra por `children`. */
export function BlocoAuditoria({ limitacoes, blocosRecebidos, blocosAusentes, idsCitados, regrasVioladas, children }: {
  limitacoes: string[];
  blocosRecebidos: string[];
  blocosAusentes: string[];
  idsCitados: (string | number)[];
  regrasVioladas: string[];
  children?: ReactNode;
}) {
  return (
    <div className="mf-subcard">
      <div className="mf-subcard-k">auditoria da rodada</div>
      {regrasVioladas.length > 0 && (
        <div className="banner" role="status">
          <span aria-hidden>⚠</span>
          <span>Regras de piso violadas: {regrasVioladas.join("; ")}</span>
        </div>
      )}
      {limitacoes.length > 0 && (
        <ul className="mf-why">
          {limitacoes.map((l, i) => <li key={i}>{l}</li>)}
        </ul>
      )}
      <details>
        <summary>o que entrou e o que foi citado</summary>
        <Facts items={[
          { k: "blocos recebidos", v: blocosRecebidos.join(", ") || "—" },
          { k: "blocos ausentes", v: blocosAusentes.join(", ") || "nenhum" },
          { k: "ids citados", v: idsCitados.join(", ") || "—" },
        ]} />
        {children}
      </details>
    </div>
  );
}

/** O rodapé de procedência: de qual linha da tabela esta tela saiu. Idêntico nas
 *  quatro sessões a menos do `agentId`. */
export function Provenancia({ user, agentId, conferencia = true }: {
  user: MargemBase;
  agentId: string;
  /* A tela genérica passa `false`: lá não há par (atual, novo) a conferir por DESENHO,
   * não por falha, e sem isto toda análise genérica com economia imprimiria "não foi
   * possível conferir a aritmética" — verdadeiro, mas lido como defeito. */
  conferencia?: boolean;
}) {
  return (
    <div className="mf-provenance">
      schema {ou(user.versao_schema)} · gerado em {fmtDateTime(user.created_at)} ·{" "}
      <code>{agentId}</code> · linha #{user.row_id ?? "—"}
      {conferencia && user.economia_confere === false && (
        <> · <strong>a economia declarada não bate com a diferença de custos</strong></>
      )}
      {/* Ha manchete, mas falta um dos custos para fechar a conta -- acontece quando o
          agente monta carteira vazia e deixa o custo dela em `null` em vez de 0. Dizer
          "nao deu para conferir" e menos do que verificar, e muito mais que calar. */}
      {conferencia && user.economia_confere === null
        && user.economia_anual_brl !== null && (
        <> · <em>não foi possível conferir a aritmética desta economia</em></>
      )}
      {" · "}
      <Link to={`/relatorios?usuario=${user.user_id}&modo=um&agente=${agentId}`}>
        abrir em Relatórios
      </Link>
    </div>
  );
}

/* ── a casca de uma sessão ─────────────────────────────────────────────────
 * A query, o <select> e as três telas de estado (carregando, erro, vazio) são iguais
 * nas quatro sessões; só mudam a chave, a rota e o rótulo da opção. */

export function SecaoMargin<U extends MargemBase>({
  chave, vazio, optionLabel, children, notaIgnoradas, acima,
}: {
  chave: string;
  vazio: ReactNode;
  optionLabel: (u: U, meses?: number) => string;
  children: (user: U) => ReactNode;
  /* O banner padrão afirma "uma rodada gravou o payload de outro agente" — coisa que
   * só quem tem `_EXIGE` pode afirmar. A tela genérica descarta por ilegibilidade, e
   * troca o texto aqui. */
  notaIgnoradas?: ReactNode;
  /* Aviso da seção inteira, acima do usuário escolhido (a genérica usa para dizer que
   * as linhas do agente não têm todas o mesmo formato). */
  acima?: (data: MargemPayload & { users: U[] }) => ReactNode;
}) {
  const [params, setParams] = useSearchParams();
  const { data: painelUsers } = useUsers();

  const q = useQuery({
    // O servidor guarda um cache de 12 h; dado novo chega pelo botão "Atualizar
    // dados", que invalida o prefixo ["margin"]. Sem revalidação por tempo: cada
    // remount desta sessão (as chips do Margin Finder desmontam as outras) NÃO pode
    // ir à rede.
    queryKey: ["margin", chave],
    queryFn: () => api<MargemPayload & { users: U[] }>(`/api/margin/${chave}`),
    staleTime: Infinity,
    gcTime: 12 * 60 * 60_000,
    retry: false,
    placeholderData: (previous) => previous,
  });
  const data = q.data;

  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  const escolhido = params.get("usuario");
  const padrao = data?.users[0]?.user_id;
  const userId = escolhido || padrao || "";
  const user = data?.users.find((u) => u.user_id === userId);

  /* FIXA o padrão na URL assim que ele é resolvido. Sem isto, `?usuario=` ausente
   * significava "o primeiro DESTA sessão" -- e como cada agente ordena por economia,
   * o primeiro é outro em cada aba: o seletor trocava de pessoa a cada chip, sem
   * ninguém ter escolhido nada. Escrevendo, a primeira seção que carrega decide, e as
   * demais herdam. `replace` para não empilhar histórico. */
  useEffect(() => {
    if (escolhido || !padrao) return;
    const next = new URLSearchParams(params);
    next.set("usuario", padrao);
    setParams(next, { replace: true });
  }, [escolhido, padrao]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Veio de outra sessão e este agente não analisou esta pessoa. Sem isto o <select>
  // fica com `value` sem <option> correspondente e o navegador o desenha em branco --
  // parece controle quebrado, quando o que houve foi ausência de dado.
  const forasteiro = Boolean(userId && data && !user);

  const label = (u: U) => {
    const conhecido = painelUsers?.find((p) => !p.is_aggregate && p.user_id === u.user_id);
    return optionLabel(u, conhecido?.months);
  };

  if (q.isPending) return <Skeleton rows={10} />;
  // Só substitui a tela pelo erro quando NÃO há dado nenhum: com retry:false, um
  // soluço no refetch disparado pelo "Atualizar dados" não pode apagar a seção
  // inteira que já estava renderizada — o dado retido continua abaixo do aviso.
  if (q.error && !data) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  if (!data) return null;

  return (
    <>
      {q.error && <ErrorState error={q.error} onRetry={() => q.refetch()} />}
      <div className="controls">
        {/* Sem usuário nenhum o <select> vazio parece controle quebrado; o "Atualizar
            dados" continua, porque é justamente o botão que pode trazer o primeiro. */}
        {data.users.length > 0 && (
          <label>usuário
            <select value={userId} onChange={(e) => set("usuario", e.target.value)}>
              {forasteiro && (
                <option value={userId} disabled>
                  {shortId(userId)}…  ·  sem análise deste agente
                </option>
              )}
              {data.users.map((u) => (
                <option key={u.user_id} value={u.user_id}>{label(u)}</option>
              ))}
            </select>
          </label>
        )}
        <AgentsRefresh fetchedAt={data.fetched_at} stale={data.stale} />
      </div>

      {data.rows_ignoradas > 0 && (
        <div className="banner" role="status">
          <span aria-hidden>⚠</span>
          <span>
            {notaIgnoradas ?? <>
              {data.rows_ignoradas} de {data.rows_scanned} linhas foram descartadas por
              não terem o formato deste agente — uma rodada gravou o payload de outro
              agente sob o mesmo <code>agent_id</code>. Os usuários abaixo usam a linha
              correta mais recente.
            </>}
          </span>
        </div>
      )}

      {acima?.(data)}

      {!user
        ? <Empty onClear={() => set("usuario", "")}>
            {forasteiro
              ? <>Este agente não analisou <code>{shortId(userId)}…</code>, o usuário
                escolhido nas outras abas. Escolha outro no seletor acima, ou limpe
                para ver o primeiro desta sessão.</>
              : vazio}
          </Empty>
        : children(user)}
    </>
  );
}

/** Um rótulo de economia para o <select>, no formato que as quatro sessões usam. */
export function economiaLabel(u: MargemBase, meses?: number) {
  const e = u.economia_anual_brl;
  const texto = e === null ? "sem economia estimada"
    : e < 0 ? `R$ ${brl2(Math.abs(e))}/ano a mais`
    : e === 0 ? "sem economia apurável"
    : `R$ ${brl2(e)}/ano`;
  return `${shortId(u.user_id)}…  ·  ${texto}${meses ? `  ·  ${meses} meses` : ""}`;
}

/** Card padrão de "lista de passos". Usado por `como_obter` e `antes_de_contratar`. */
export function Passos({ title, sub, items }: { title: string; sub?: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <Card title={title} sub={sub}>
      <ol className="mf-checklist">
        {items.map((s, i) => <li key={i}>{s}</li>)}
      </ol>
    </Card>
  );
}
